/* global console window URL Blob */

import { isEmbeddableCrossOriginIframe } from "./embedBridge";

// When embeddable.html runs inside a cross-origin iframe, the browser blocks
// showSaveFilePicker. We delegate the call to the parent frame via postMessage
// (the same pattern used for proxy-fetch). The parent holds the FileSystemFileHandle.

export const SAVE_FILE_REQUEST_TYPE = "gridd-save-file";
export const SAVE_FILE_RESULT_TYPE = "gridd-save-file-result";
export const SAVE_TO_HANDLE_TYPE = "gridd-save-to-handle";

function fileNameFromLocalStorageKey(localStorageKey) {
  if (!localStorageKey) return "gridd.json";
  const uri = localStorageKey.replace(/_GRIDD$/, "");
  try {
    const rawName = new URL(uri).pathname.split("/").filter(Boolean).pop() || "gridd.json";
    //tech-demo-randomdocosuaurusstring.json --> tech-demo.json
    if (rawName.includes("-") && rawName.endsWith(".json")) {
      const lastDash = rawName.lastIndexOf("-");
      const firstPart = rawName.slice(0, lastDash);
      return firstPart + ".json";
    }
  } catch {
    return "gridd.json";
  }
}

let pendingSaveId = 0;
const pendingSaveResolvers = new Map();

if (typeof window !== "undefined") {
  window.addEventListener("message", (event) => {
    const data = event.data;
    if (!data || typeof data !== "object" || data.type !== SAVE_FILE_RESULT_TYPE) return;
    const resolver = pendingSaveResolvers.get(data.id);
    if (!resolver) return;
    pendingSaveResolvers.delete(data.id);
    resolver(data);
  });
}

function saveViaParent(json, suggestedName) {
  return new Promise((resolve, reject) => {
    const id = ++pendingSaveId;
    pendingSaveResolvers.set(id, (result) => {
      // treat abort as a silent cancel, not an error
      if (result.ok || result.aborted) resolve();
      else reject(new Error(result.error || "Save failed"));
    });
    window.parent.postMessage({ type: SAVE_FILE_REQUEST_TYPE, id, data: json, suggestedName }, "*");
  });
}

//FileSystemFileHandle cannot be serialized, so it cannot be added to the Redux store
//see https://github.com/WICG/file-system-access/issues/368
let fileHandle = null;

export const exportGriddDataToJSON = (state) => {
  const gridd = {
    cols: state.gridd.present.structure.columns,
    rows: state.gridd.present.structure.rows,
    cells: state.gridd.present.structure.cells,
    borders: state.gridd.present.borders,
    cellStyles: state.gridd.present.cellStyles,
    spacerStyles: state.gridd.present.spacerStyles,
    merged: state.gridd.present.structure.merged,
  };
  return JSON.stringify(gridd);
};

const writeToHandle = async (handle, json) => {
  const blob = new Blob([json], { type: "application/json" });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
};

export const saveToExistingHandle = async (state) => {
  const json = exportGriddDataToJSON(state);
  if (isEmbeddableCrossOriginIframe()) {
    window.parent.postMessage({ type: SAVE_TO_HANDLE_TYPE, data: json }, "*");
    return;
  }
  if (!fileHandle) return;
  try {
    await writeToHandle(fileHandle, json);
    console.log("gridd auto-saved to:", fileHandle.name);
  } catch (e) {
    console.log("error auto-saving file", e);
  }
};

export const saveGriddToFile = async (state) => {
  const suggestedName = fileNameFromLocalStorageKey(state.app.localStorageKey);
  const json = exportGriddDataToJSON(state);
  if (isEmbeddableCrossOriginIframe()) {
    try {
      await saveViaParent(json, suggestedName);
      console.log("gridd saved via parent iframe bridge");
    } catch (e) {
      console.log("error saving file via parent", e);
      throw e;
    }
    return;
  }

  if (window.showSaveFilePicker) {
    if (!fileHandle) {
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName,
          types: [{ description: "JSON file", accept: { "application/json": [".json"] } }],
        });
      } catch (e) {
        if (e.name === "AbortError") {
          console.log("user aborted the save");
          return;
        }
        console.log("error saving file", e);
        throw e;
      }
    }
    await writeToHandle(fileHandle, json);
    console.log("gridd data file saved to:", fileHandle.name);
  } else {
    // Fallback: always triggers a download
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = suggestedName;
    a.click();
    URL.revokeObjectURL(url);
  }
};
